import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  createHttpLink,
} from '@apollo/client';
import {loadErrorMessages, loadDevMessages} from '@apollo/client/dev';
import React from 'react';
import {createPreviewInvalidationLink} from 'src/apolloCache/previews';
import {
  createSuggestionInvalidationLink,
  suggestionsByRow,
} from 'src/apolloCache/suggestions';
import type {FieldPolicy} from '@apollo/client';

loadDevMessages();
loadErrorMessages();

type PaginatedField = {items: unknown[]};

// Cursor-paginated list fields accumulate their pages under a single cache
// entry keyed by the filter args (not the cursor), so navigating back to a
// list re-renders everything the user had loaded. A fetch without a cursor
// is a fresh first page and replaces the entry.
const cursorPagination = (keyArgs: string[]): FieldPolicy<PaginatedField> => ({
  keyArgs,
  merge: (existing, incoming, {args}) =>
    args?.beforeCursor && existing
      ? {...incoming, items: [...existing.items, ...incoming.items]}
      : incoming,
});

type Props = {
  children?: React.ReactNode;
};
const _ApolloProvider = (props: Props) => {
  // The client (and its cache) must be created exactly once: pages rely on
  // the cache surviving navigation (e.g. scroll restoration re-renders
  // previously loaded list pages from it), so a re-render of this provider
  // must not rebuild the client and silently drop all cached data.
  const client = React.useMemo(() => {
    // Auth rides on the httpOnly session cookie (BFF), so the request needs
    // no auth header — just credentials. The URI is relative in dev too:
    // the Parcel dev server proxies /graphql to the backend (.proxyrc.json)
    // so cookies are first-party in both environments.
    const httpLink = createHttpLink({
      uri: '/graphql',
      credentials: 'include',
    });
  const cache = new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          // Every filter arg must be listed: a missing one merges queries
          // that differ only in that arg into ONE cache entry, so whichever
          // fetch lands last clobbers the other's rows. That is how the
          // needs-basis inbox card went blank whenever a picker or list
          // refetch ran — TransactionsNeedingBasis shared its entry with
          // unfiltered queries.
          transactions: cursorPagination([
            'categories',
            'tags',
            'search',
            'fieldFilters',
            'netAmountFilter',
            'kinds',
            'minAmount',
            'maxAmount',
            'pending',
            'needsBasis',
            'startDate',
            'endDate',
            'transferTypes',
            'excludeTransferTypes',
            'currency',
            'limit',
          ]),
          suggestCategorizations: suggestionsByRow,
        },
      },
      RawTransactions: {
        keyFields: ['_id'],
      },
      Accounts: {
        keyFields: ['_id'],
      },
      // PlaidConnectionInfo has no id, so it's stored embedded in its parent
      // account. Different queries select different plaid subsets (the Account
      // fragment vs AccountPlaidConnection); without a merge policy each write
      // would replace the object and drop the other query's fields, leaving
      // watchers partial and triggering spurious refetches.
      DepositoryAccount: {
        fields: {
          plaid: {merge: true},
        },
      },
      // performance is likewise embedded (InvestmentPerformance has no id)
      // and is written both by AllInvestmentAccountsPerformance and the full
      // InvestmentAccountFragment; merge field-wise so a write from one
      // never drops fields the other's watchers selected.
      InvestmentAccount: {
        fields: {
          plaid: {merge: true},
          performance: {merge: true},
        },
      },
    },
    possibleTypes: {
      TransactionUnion: [
        'PaymentTransaction',
        'TransferTransaction',
      ],
      AccountUnion: ['DepositoryAccount', 'InvestmentAccount'],
    },
  });
    return new ApolloClient({
      // Preview invalidation rides the link chain so EVERY mutation clears
      // cached homeAmountPreview scalars — see apolloCache/previews for why
      // this is not done per call site.
      link: createPreviewInvalidationLink(cache)
        .concat(createSuggestionInvalidationLink(cache))
        .concat(httpLink),
      cache,
      devtools: {
        enabled: true,
      },
    });
  }, []);
  return <ApolloProvider client={client}>{props.children}</ApolloProvider>;
};

export default _ApolloProvider;
